import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { Plus, Trash2, Check, X, BookOpen } from 'lucide-react';

const CATALOG_TYPES = [
  { key: 'persona',        label: 'Personas',             description: 'Miembros de la familia' },
  { key: 'categoria',      label: 'Categorías de Gastos', description: 'Categorías usadas en Gastos' },
  { key: 'medioPago',      label: 'Medios de Pago',       description: 'Formas de pago disponibles' },
  { key: 'tipoIngresos',   label: 'Tipos de Ingreso',     description: 'Clasificación de ingresos' },
  { key: 'tipoPatrimonio', label: 'Tipos de Patrimonio',  description: 'Tipos de activos y pasivos' },
];

const SEED_DATA = {
  persona:        ['Tumi', 'Alberto'],
  categoria:      ['Caja de Ahorro', 'Carmen', 'Bupa', 'Vacaciones', 'Gym & Entrenador', 'Takiro',
                   'Servicios', 'Gasolina', 'Comida', 'Préstamos', 'Cuota Be Grand', 'Mani Pedi',
                   'Pelu', 'Gastos Pequeños Diarios', 'ITESM Camila', 'ITESM Tony', 'Renta MTY',
                   'Mensualidad Camila y Tony', 'AT&T', 'Starlink', 'EALDE (256 Euros)', 'Netflix',
                   'LinkedIn', 'Youtube', 'Préstamo Tumi', 'Barber', 'Salidas', 'Uber'],
  medioPago:      ['Nómina', 'Débito', 'TDC', 'Efectivo'],
  tipoIngresos:   ['Nómina', 'Inversión', 'Bonos', 'Prima Vacacional', 'Otros', 'CETES'],
  tipoPatrimonio: ['Dólares', 'Caja de Ahorro', 'AFORE', 'Inversión', 'Inmueble', 'CETES',
                   'Plan de Retiro', 'Préstamo Nómina', 'Tarjeta de Crédito', 'Crédito Personal',
                   'Negocio', 'Efectivo'],
};

export default function Catalogos({ db }) {
  const [activeTab, setActiveTab] = useState('persona');
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newValue, setNewValue] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, 'catalogos'));
    const grouped = {};
    CATALOG_TYPES.forEach(t => { grouped[t.key] = []; });

    snap.docs.forEach(d => {
      const data = d.data();
      if (grouped[data.tipo] !== undefined) {
        grouped[data.tipo].push({ id: d.id, valor: data.valor, orden: data.orden || 0 });
      }
    });

    // Sort each group
    Object.keys(grouped).forEach(k => grouped[k].sort((a, b) => a.orden - b.orden));

    // Seed any empty groups
    for (const type of CATALOG_TYPES) {
      if (grouped[type.key].length === 0) {
        const seeds = SEED_DATA[type.key] || [];
        for (let i = 0; i < seeds.length; i++) {
          const ref = await addDoc(collection(db, 'catalogos'), { tipo: type.key, valor: seeds[i], orden: i });
          grouped[type.key].push({ id: ref.id, valor: seeds[i], orden: i });
        }
      }
    }

    setItems(grouped);
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!newValue.trim()) return;
    const orden = (items[activeTab] || []).length;
    const ref = await addDoc(collection(db, 'catalogos'), { tipo: activeTab, valor: newValue.trim(), orden });
    setItems(prev => ({
      ...prev,
      [activeTab]: [...(prev[activeTab] || []), { id: ref.id, valor: newValue.trim(), orden }],
    }));
    setNewValue('');
    setAdding(false);
  };

  const handleSaveEdit = async (id) => {
    if (!editValue.trim()) return;
    await updateDoc(doc(db, 'catalogos', id), { valor: editValue.trim() });
    setItems(prev => ({
      ...prev,
      [activeTab]: prev[activeTab].map(i => i.id === id ? { ...i, valor: editValue.trim() } : i),
    }));
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este valor?')) return;
    await deleteDoc(doc(db, 'catalogos', id));
    setItems(prev => ({ ...prev, [activeTab]: prev[activeTab].filter(i => i.id !== id) }));
  };

  const currentItems = items[activeTab] || [];
  const currentTab = CATALOG_TYPES.find(t => t.key === activeTab);

  if (loading) return (
    <div className="page-container" style={{ textAlign: 'center', paddingTop: '3rem' }}>
      <div className="spinner" style={{ margin: '0 auto' }} />
      <p style={{ marginTop: '1rem', color: '#6b7280' }}>Cargando catálogos...</p>
    </div>
  );

  return (
    <div className="page-container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <BookOpen size={28} color="#1a472a" />
        <h2 className="page-title" style={{ margin: 0 }}>Catálogos</h2>
      </div>

      {/* Tab selector */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {CATALOG_TYPES.map(t => (
          <button
            key={t.key}
            className={`filter-btn ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => { setActiveTab(t.key); setAdding(false); setEditingId(null); setNewValue(''); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Catalog card */}
      <div style={{ background: 'var(--bg-secondary)', borderRadius: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ background: '#1a472a', padding: '1rem 1.5rem' }}>
          <h3 style={{ color: 'white', margin: 0, fontSize: '1.1rem' }}>{currentTab?.label}</h3>
          <p style={{ color: 'rgba(255,255,255,0.7)', margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
            {currentTab?.description} — {currentItems.length} valores
          </p>
        </div>

        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '3rem' }}>#</th>
              <th>Valor</th>
              <th style={{ width: '6rem', textAlign: 'center' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((item, idx) => (
              <tr key={item.id}>
                <td style={{ color: '#9ca3af', fontSize: '0.85rem' }}>{idx + 1}</td>
                <td>
                  {editingId === item.id ? (
                    <input
                      className="table-input"
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(item.id); if (e.key === 'Escape') setEditingId(null); }}
                      onBlur={() => handleSaveEdit(item.id)}
                      autoFocus
                      style={{ border: '2px solid #1a472a', borderRadius: '0.25rem', padding: '0.25rem 0.5rem', width: '100%' }}
                    />
                  ) : (
                    <span
                      style={{ cursor: 'pointer', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', display: 'inline-block' }}
                      onDoubleClick={() => { setEditingId(item.id); setEditValue(item.valor); }}
                      title="Doble clic para editar"
                    >
                      {item.valor}
                    </span>
                  )}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                    {editingId === item.id ? (
                      <>
                        <button className="btn-icon" onMouseDown={() => handleSaveEdit(item.id)} title="Guardar">
                          <Check size={16} color="#10b981" />
                        </button>
                        <button className="btn-icon" onMouseDown={() => setEditingId(null)} title="Cancelar">
                          <X size={16} color="#6b7280" />
                        </button>
                      </>
                    ) : (
                      <button className="btn-icon danger" onClick={() => handleDelete(item.id)} title="Eliminar">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}

            {adding && (
              <tr style={{ background: 'rgba(16,185,129,0.08)' }}>
                <td style={{ color: '#9ca3af' }}>{currentItems.length + 1}</td>
                <td>
                  <input
                    className="table-input"
                    value={newValue}
                    onChange={e => setNewValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { setAdding(false); setNewValue(''); } }}
                    placeholder="Nuevo valor..."
                    autoFocus
                    style={{ border: '2px solid #10b981', borderRadius: '0.25rem', padding: '0.25rem 0.5rem', width: '100%' }}
                  />
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                    <button className="btn-icon" onClick={handleAdd} title="Agregar"><Check size={16} color="#10b981" /></button>
                    <button className="btn-icon" onClick={() => { setAdding(false); setNewValue(''); }} title="Cancelar"><X size={16} color="#6b7280" /></button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--border-color)' }}>
          {!adding && (
            <button className="btn-add-row" onClick={() => setAdding(true)}>
              <Plus size={16} /> Agregar valor
            </button>
          )}
        </div>
      </div>

      <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#9ca3af', textAlign: 'center' }}>
        Doble clic en un valor para editarlo. Los cambios se guardan automáticamente.
      </p>
    </div>
  );
}
